import type { AnswersByKey, PaymentPlanOption } from "./types";
import type { PaywallEntryPoint } from "./paywallEntryPoint";

export const PAYWALL_VARIANTS = ["control", "personalized_14d", "annual_trial_3d"] as const;
export type PaywallVariant = (typeof PAYWALL_VARIANTS)[number];

export const PAYWALL_SCENARIOS = [
  "default",
  "starter_short",
  "deeper_guided",
  "family_story"
] as const;
export type PaywallScenario = (typeof PAYWALL_SCENARIOS)[number];

export type PaywallPersonalization = {
  variant: PaywallVariant;
  scenario: PaywallScenario;
  entryPoint: PaywallEntryPoint;
  experience: "quiz" | "recovery" | "mobile_app" | "lifecycle_email";
  isRecovery: boolean;
  recommendedPlanId: string;
  eyebrow: string;
  title: string;
  description: string;
  answerRecap: string[];
  fourteenDayItems: Array<{ range: string; title: string; description: string }>;
  recoveryNote?: string;
};

const asString = (value: AnswersByKey[string]): string =>
  typeof value === "string" ? value : "";

const asArray = (value: AnswersByKey[string]): string[] =>
  Array.isArray(value) ? value : typeof value === "string" ? [value] : [];

export const derivePaywallScenario = (answers: AnswersByKey): PaywallScenario => {
  const audience = asString(answers.saakhi_audience);
  if (audience === "family" || audience === "kids") {
    return "family_story";
  }

  const familiarity = asString(answers.saakhi_familiarity);
  const guidance = asString(answers.guidance_style);
  const priorities = asArray(answers.practice_priorities);

  if (
    familiarity === "no_idea" &&
    (guidance === "short_anchor" || priorities.includes("five_min_routine"))
  ) {
    return "starter_short";
  }

  if (
    guidance === "deeper_guided" ||
    priorities.includes("deeper_saakhis")
  ) {
    return "deeper_guided";
  }

  return "default";
};

const normalizeVariant = (value: string | null): PaywallVariant =>
  PAYWALL_VARIANTS.includes(value as PaywallVariant)
    ? (value as PaywallVariant)
    : "control";

const normalizeScenario = (value: string | null): PaywallScenario =>
  PAYWALL_SCENARIOS.includes(value as PaywallScenario)
    ? (value as PaywallScenario)
    : "default";

export const resolvePaywallQaContextFromUrl = (
  urlLike: URL | string = window.location.href,
  allowOverrides = import.meta.env.DEV
): { variant: PaywallVariant; scenario: PaywallScenario; planId: string | null; hasOverride: boolean } => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const requestedVariant = normalizeVariant(url.searchParams.get("paywall_variant"));
  const hasPublicTrialOverride = requestedVariant === "annual_trial_3d";
  const hasQaOverride = allowOverrides && (
    url.searchParams.has("paywall_variant") ||
    url.searchParams.has("paywall_scenario") ||
    url.searchParams.has("plan")
  );
  const hasOverride = hasPublicTrialOverride || hasQaOverride;
  const planId = url.searchParams.get("plan");
  return {
    variant: hasOverride ? requestedVariant : "control",
    scenario: hasQaOverride ? normalizeScenario(url.searchParams.get("paywall_scenario")) : "default",
    planId: hasPublicTrialOverride
      ? "annual"
      : hasQaOverride && (planId === "weekly" || planId === "monthly" || planId === "quarterly" || planId === "annual")
        ? planId
        : null,
    hasOverride
  };
};

const SCENARIO_COPY: Record<
  PaywallScenario,
  Omit<PaywallPersonalization, "variant" | "scenario" | "entryPoint" | "experience" | "isRecovery" | "recoveryNote">
> = {
  default: {
    recommendedPlanId: "quarterly",
    eyebrow: "Your private Sehaj path",
    title: "Your 14-day Sehaj rhythm is ready",
    description: "A calm, structured beginning shaped around the answers you shared.",
    answerRecap: [
      "One short daily Saakhi anchor",
      "A clear reflection you can carry into the day",
      "A gentle rhythm designed to make consistency easier"
    ],
    fourteenDayItems: [
      { range: "Days 1–3", title: "Begin with Bhai Lalo Ji", description: "Read the Saakhi and connect its meaning to ordinary life." },
      { range: "Days 4–7", title: "Create a daily anchor", description: "Pair one short story moment with a simple Simran prompt." },
      { range: "Days 8–14", title: "Make the rhythm yours", description: "Notice what helps you return without pressure or perfection." }
    ]
  },
  starter_short: {
    recommendedPlanId: "quarterly",
    eyebrow: "No prior Saakhi knowledge needed",
    title: "Start gently. Understand deeply",
    description: "Your answers point to a simple path: clear explanations, a short daily anchor, and room to grow at your pace.",
    answerRecap: [
      "Bhai Lalo Ji explained from the beginning",
      "A five-minute daily rhythm that fits real life",
      "Deeper understanding without feeling behind"
    ],
    fourteenDayItems: [
      { range: "Days 1–3", title: "The story, made clear", description: "Meet Bhai Lalo Ji through the full Saakhi and one grounded reflection." },
      { range: "Days 4–7", title: "Five minutes of stillness", description: "Build a repeatable Saakhi and Simran anchor for busy days." },
      { range: "Days 8–14", title: "Understanding that stays", description: "Connect each lesson to one practical choice in daily life." }
    ]
  },
  deeper_guided: {
    recommendedPlanId: "quarterly",
    eyebrow: "For the depth you asked for",
    title: "Go beyond hearing the story",
    description: "Your path adds context, guided reflection, and a short practice so insight becomes something lived.",
    answerRecap: [
      "More context around each Saakhi",
      "Guided questions that turn insight into action",
      "A short structure that keeps depth sustainable"
    ],
    fourteenDayItems: [
      { range: "Days 1–3", title: "Story and context", description: "Read Bhai Lalo Ji with the people, place, and principle made clear." },
      { range: "Days 4–7", title: "Reflect with guidance", description: "Use focused prompts to notice how the teaching appears today." },
      { range: "Days 8–14", title: "Practice the principle", description: "Turn understanding into one calm, repeatable daily action." }
    ]
  },
  family_story: {
    recommendedPlanId: "quarterly",
    eyebrow: "A path you can share",
    title: "Bring one meaningful Saakhi into family life",
    description: "Your path makes the story clear for you first, then gives you a simple way to share it without turning it into a lesson.",
    answerRecap: [
      "A complete Saakhi in accessible language",
      "One optional family conversation prompt",
      "A short daily ritual that works around real schedules"
    ],
    fourteenDayItems: [
      { range: "Days 1–3", title: "Read together or alone", description: "Begin with Bhai Lalo Ji and a question suitable for family reflection." },
      { range: "Days 4–7", title: "Make space for one thought", description: "Use a short prompt at breakfast, bedtime, or a quiet moment." },
      { range: "Days 8–14", title: "Build a shared rhythm", description: "Find the pace that feels natural for your household." }
    ]
  }
};

const APP_ACCESS_COPY: Omit<
  PaywallPersonalization,
  "variant" | "scenario" | "entryPoint" | "experience" | "isRecovery" | "recoveryNote"
> = {
  recommendedPlanId: "quarterly",
  eyebrow: "Continue from the Sehaj app",
  title: "Unlock your full Sehaj library",
  description: "Choose your plan here, then return to the app. Your signed-in account will recognise your access after checkout.",
  answerRecap: [
    "Every published Saakhi in one quiet library",
    "English and Punjabi reading and listening where available",
    "Access connected to the account already signed in on your app"
  ],
  fourteenDayItems: [
    { range: "Today", title: "Choose your plan", description: "Complete secure checkout once, then reopen Sehaj." },
    { range: "First week", title: "Begin with one Saakhi", description: "Read or listen without needing to repeat the quiz." },
    { range: "Ongoing", title: "Return at your pace", description: "Keep your library, reflections, and journey together in the app." }
  ]
};

const ANNUAL_TRIAL_COPY: Omit<
  PaywallPersonalization,
  "variant" | "scenario" | "entryPoint" | "experience" | "isRecovery" | "recoveryNote"
> = {
  recommendedPlanId: "annual",
  eyebrow: "ਇੱਕ ਨਿੱਜੀ ਰਿਥਮ · A private daily rhythm",
  title: "Begin with 3 days free",
  description: "Private, grounded guidance for a daily Sikhi ritual that sticks. No noise. No pressure.",
  answerRecap: [],
  fourteenDayItems: []
};

const NON_TRIAL_RECOVERY_COPY: typeof ANNUAL_TRIAL_COPY = {
  recommendedPlanId: "quarterly",
  eyebrow: "Your selected membership",
  title: "Continue with your chosen plan",
  description: "Review the live price below, then continue securely when you are ready.",
  answerRecap: [],
  fourteenDayItems: []
};

export const buildPaywallPersonalization = (input: {
  variant: PaywallVariant;
  scenario: PaywallScenario;
  entryPoint?: PaywallEntryPoint;
  isRecovery?: boolean;
  recommendedPlanId?: string | null;
}): PaywallPersonalization => {
  const entryPoint = input.entryPoint ?? "funnel";
  const isAppEntry = entryPoint === "mobile_app";
  const copy = input.variant === "annual_trial_3d"
    ? input.isRecovery && input.recommendedPlanId && input.recommendedPlanId !== "annual"
      ? NON_TRIAL_RECOVERY_COPY
      : ANNUAL_TRIAL_COPY
    : isAppEntry
      ? APP_ACCESS_COPY
      : SCENARIO_COPY[input.scenario];
  return {
    ...copy,
    variant: input.variant,
    scenario: input.scenario,
    entryPoint,
    experience: isAppEntry
      ? "mobile_app"
      : input.isRecovery
        ? "recovery"
        : entryPoint === "lifecycle_email"
          ? "lifecycle_email"
          : "quiz",
    isRecovery: Boolean(input.isRecovery),
    recommendedPlanId: input.recommendedPlanId ?? copy.recommendedPlanId,
    ...(input.isRecovery
      ? { recoveryNote: "Your selected plan is restored below. Review it, then continue when you feel ready." }
      : {})
  };
};

export const getRecommendedPlan = (
  plans: PaymentPlanOption[],
  context: PaywallPersonalization | null
): PaymentPlanOption | null => {
  if (!context) return null;
  return plans.find((plan) => plan.id === context.recommendedPlanId) ?? null;
};
